import React from 'react';
import { useCall } from '@/contexts/CallContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Phone, PhoneOff, Mic, MicOff } from 'lucide-react';

/**
 * Format duration in seconds to MM:SS format
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Get status badge color and text based on call status
 */
function getStatusDisplay(status: string) {
  switch (status) {
    case 'connecting':
      return { text: 'Connecting...', color: 'bg-yellow-500' };
    case 'ringing':
      return { text: 'Ringing...', color: 'bg-blue-500 animate-pulse' };
    case 'in-progress':
      return { text: 'In Call', color: 'bg-green-500' };
    default:
      return { text: 'Unknown', color: 'bg-gray-500' };
  }
}

/**
 * Floating call panel that appears during active calls
 * Fixed position at bottom-right of screen
 * Displays call status, customer info, duration, and controls
 */
export function FloatingCallPanel() {
  const callContext = useCall();

  // Don't render if context not available (initialization) or no active call
  if (!callContext || !callContext.activeCall) {
    return null;
  }

  const { activeCall, isMuted, hangup, toggleMute } = callContext;

  const statusDisplay = getStatusDisplay(activeCall.status);

  return (
    <div className="fixed bottom-6 right-6 z-[9999] animate-in slide-in-from-bottom-5 duration-300">
      <Card className="w-80 shadow-2xl border-2 border-primary/20">
        <CardContent className="p-4 space-y-3">
          {/* Header: Status Badge */}
          <div className="flex items-center justify-between">
            <Badge className={`${statusDisplay.color} text-white px-3 py-1`}>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white" />
                {statusDisplay.text}
              </div>
            </Badge>
            <div className="text-2xl font-mono font-bold text-primary">
              {formatDuration(activeCall.duration)}
            </div>
          </div>

          {/* Customer Info */}
          <div className="space-y-1">
            <h3 className="font-semibold text-lg truncate">{activeCall.customerName}</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="h-3 w-3" />
              <span className="font-mono">{activeCall.customerPhone}</span>
            </div>
          </div>

          {/* Call Controls */}
          <div className="flex gap-2 pt-2">
            {/* Mute Button */}
            <Button
              variant={isMuted ? 'default' : 'outline'}
              size="sm"
              onClick={toggleMute}
              className="flex-1"
              disabled={activeCall.status !== 'in-progress'}
            >
              {isMuted ? (
                <>
                  <MicOff className="h-4 w-4 mr-2" />
                  Unmute
                </>
              ) : (
                <>
                  <Mic className="h-4 w-4 mr-2" />
                  Mute
                </>
              )}
            </Button>

            {/* Hangup Button */}
            <Button
              variant="destructive"
              size="sm"
              onClick={hangup}
              className="flex-1"
            >
              <PhoneOff className="h-4 w-4 mr-2" />
              End Call
            </Button>
          </div>

          {/* Status Message */}
          {activeCall.status === 'connecting' && (
            <p className="text-xs text-center text-muted-foreground">
              Initializing call...
            </p>
          )}
          {activeCall.status === 'ringing' && (
            <p className="text-xs text-center text-muted-foreground">
              Waiting for customer to answer...
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
